import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Search, Zap, BrainCircuit, Sparkles, LayoutDashboard, ShoppingCart, Code, AlertCircle, CheckCircle2, Clock, Plus, X } from 'lucide-react';

interface Request {
  id: number;
  title: string;
  category: string;
  votes: number;
  date: string;
  status: 'new' | 'under-review' | 'planned';
  progress: number;
  description: string;
}

const TemplateRequestsPage = () => {
  const [requests, setRequests] = useState<Request[]>([
    { 
      id: 1, 
      title: "AI-Powered Analytics Suite", 
      category: "dashboard", 
      votes: 45, 
      date: "2024-03-15", 
      status: "under-review", 
      progress: 65,
      description: "Advanced analytics platform with real-time AI insights and predictive modeling capabilities." 
    },
    { 
      id: 2, 
      title: "Next-Gen E-commerce Platform", 
      category: "ecommerce", 
      votes: 32, 
      date: "2024-03-18", 
      status: "planned", 
      progress: 20,
      description: "Omnichannel commerce solution with AR integration and AI-powered recommendations." 
    }
  ]);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', category: 'dashboard', description: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [existingRequest, setExistingRequest] = useState<Request | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

  const categories = [
    { value: 'dashboard', label: 'AI Dashboards', icon: <LayoutDashboard className="w-5 h-5" />, color: 'bg-purple-500' },
    { value: 'ecommerce', label: 'Smart Commerce', icon: <ShoppingCart className="w-5 h-5" />, color: 'bg-blue-500' },
    { value: 'iot', label: 'IoT Solutions', icon: <Code className="w-5 h-5" />, color: 'bg-green-500' }
  ];

  const statusMeta = {
    'new': { label: 'Nuevo', icon: <Clock className="w-4 h-4" />, color: 'text-blue-400' },
    'under-review': { label: 'En Desarrollo', icon: <AlertCircle className="w-4 h-4" />, color: 'text-yellow-400' },
    'planned': { label: 'Próximo Lanzamiento', icon: <CheckCircle2 className="w-4 h-4" />, color: 'text-green-400' }
  };

  useEffect(() => {
    if (formData.title.length > 2) {
      const existing = requests.find(r => r.title.toLowerCase() === formData.title.toLowerCase());
      setExistingRequest(existing || null);
    }
  }, [formData.title, requests]);

  const OpenModal = () => {
    setIsFormOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newRequest: Request = {
      id: Date.now(),
      ...formData,
      votes: 0,
      date: new Date().toISOString().split('T')[0],
      status: 'new',
      progress: 0
    };
    setRequests([newRequest, ...requests]);
    setIsFormOpen(false);
    setFormData({ title: '', category: 'dashboard', description: '' });
  };

  const handleVote = (id: number) => {
    setRequests(requests.map(r => 
      r.id === id ? { ...r, votes: r.votes + 1, progress: Math.min(r.progress + 5, 100) } : r
    ));
  };

  const showDetails = (request: Request) => {
    setSelectedRequest(request);
  };

  const filteredRequests = requests.filter(r => 
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
    (selectedCategory === 'all' || r.category === selectedCategory)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-navy-900 text-gray-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Rocket className="w-16 h-16 mx-auto text-cyan-400 mb-6" />
            <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Innovation Pipeline
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Colabora en dar forma al futuro de nuestras soluciones tecnológicas
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="inline-block mt-12"
          >
            <button  type='button'
              onClick={OpenModal}
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-3 transition-all"
            >
              <Plus className="w-5 h-5" />
              Nueva Idea
            </button>
          </motion.div>
        </div>

        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-1/3 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-100" />
        </div>
      </div>

      {/* Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-gray-800/90 border border-gray-700/30 rounded-2xl p-8 max-w-2xl w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-white"
                onClick={() => setIsFormOpen(false)}
              >
                <X className="w-6 h-6" />
              </button>

              <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-8">
                <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                  <Sparkles className="text-cyan-400" />
                  Propuesta de Innovación
                </h2>
                
                {existingRequest && (
                  <div className="bg-yellow-900/30 p-4 rounded-xl mb-6 flex items-center gap-3 border border-yellow-800/50">
                    <AlertCircle className="text-yellow-400" />
                    <div>
                      <p className="font-medium">Propuesta existente:</p>
                      <p className="text-sm text-yellow-300">{existingRequest.title}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Título de la Propuesta</label>
                    <input
                      type="text"
                      required
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-5 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      value={formData.title}
                      onChange={e => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Categoría</label>
                    <div className="grid grid-cols-2 gap-4">
                      {categories.map(category => (
                        <label
                          key={category.value}
                          className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                            formData.category === category.value 
                              ? 'bg-gray-700/50 border border-cyan-500/30' 
                              : 'bg-gray-800/30 hover:bg-gray-700/50 border border-gray-700/30'
                          }`}
                        >
                          <input
                            type="radio"
                            name="category"
                            value={category.value}
                            checked={formData.category === category.value}
                            onChange={() => setFormData({ ...formData, category: category.value })}
                            className="hidden"
                          />
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${category.color}`}>
                            {category.icon}
                          </div>
                          <span className="font-medium">{category.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-3">Detalles de Implementación</label>
                    <textarea
                      rows={4}
                      required
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-5 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-all"
                  >
                    <BrainCircuit className="w-5 h-5" />
                    Enviar Propuesta
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interactive Pipeline Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-24">
        <div className="mb-12 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <Zap className="text-cyan-400" />
            Proyectos en Desarrollo
          </h2>
          
          <div className="flex gap-4 w-full md:w-auto">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Buscar proyectos..."
                className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 focus:ring-2 focus:ring-cyan-500"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-4 top-3.5 text-gray-500" />
            </div>
            <select
              className="bg-gray-800/50 border border-gray-700 rounded-xl px-4 py-3"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
            >
              <option value="all">Todas las categorías</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRequests.map(request => (
            <motion.div
              key={request.id}
              whileHover={{ y: -10 }}
              className="relative bg-gray-800/50 border border-gray-700/30 rounded-2xl p-6 backdrop-blur-lg overflow-hidden cursor-pointer"
              onClick={() => showDetails(request)}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 opacity-0 transition-opacity duration-300 hover:opacity-100" />
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${categories.find(c => c.value === request.category)?.color}`}>
                    {categories.find(c => c.value === request.category)?.icon}
                  </div>
                  <span className={`flex items-center gap-2 text-sm px-3 py-1 rounded-full ${statusMeta[request.status].color} bg-gray-900/50`}>
                    {statusMeta[request.status].icon}
                    {statusMeta[request.status].label}
                  </span>
                </div>

                <h3 className="text-xl font-bold mb-3">{request.title}</h3>
                <div className="h-24 mb-6">
                  <div className="text-sm text-gray-400">Progreso de Desarrollo</div>
                  <div className="w-full bg-gray-700/50 rounded-full h-2 mt-2">
                    <div
                      className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${request.progress}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-400">{request.date}</div>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVote(request.id);
                    }}
                    className="flex items-center gap-2 bg-cyan-600/30 hover:bg-cyan-600/50 px-4 py-2 rounded-xl transition-all"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="font-medium">{request.votes}</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-lg z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedRequest(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-gray-800/90 border border-gray-700/30 rounded-2xl p-8 max-w-2xl w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-white"
                onClick={() => setSelectedRequest(null)}
              >
                <X className="w-6 h-6" />
              </button>

              <div className="mb-6">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${categories.find(c => c.value === selectedRequest.category)?.color} mb-4`}>
                  {categories.find(c => c.value === selectedRequest.category)?.icon}
                </div>
                <h3 className="text-2xl font-bold mb-2">{selectedRequest.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <span className={`text-sm ${statusMeta[selectedRequest.status].color}`}>
                    {statusMeta[selectedRequest.status].icon} {statusMeta[selectedRequest.status].label}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-400">{selectedRequest.date}</span>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-cyan-400 mb-2">Descripción del Proyecto</h4>
                  <p className="text-gray-300">{selectedRequest.description}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-cyan-400 mb-2">Progreso Actual</h4>
                  <div className="w-full bg-gray-700/50 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-cyan-400 to-blue-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${selectedRequest.progress}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-cyan-400 mb-2">Votos de la Comunidad</h4>
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-yellow-400" />
                      <span className="text-xl font-bold">{selectedRequest.votes}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleVote(selectedRequest.id)}
                    className="bg-cyan-600/30 hover:bg-cyan-600/50 px-6 py-3 rounded-xl flex items-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    Apoyar Proyecto
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TemplateRequestsPage;